import { type ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import { stripLocaleFromPathname } from '@shared/i18n';
import { useDoctorControlStore } from '@shared/stores/doctor-control-store';
import { AblyChatProvider } from './AblyChatProvider';
import {
  getMessagingIdentitySource,
  getMessagingParticipantUserId,
  getMessagingAuthorizationRoomIds,
  getMessagingRealtimeRoomIds,
} from './messagingIdentity';
import { RoomNotificationSubscriptions } from '../components/RoomNotificationSubscriptions';
import { useMessagingRooms } from '../hooks/useMessagingApi';

export function GlobalMessagingRealtimeProvider({ children }: { children: ReactNode }) {
  const {
    user,
    authContext,
    actorUser,
    actorAuthContext,
    isDoctorControlMode,
    isAuthenticated,
    isLoading,
  } = useAuth();
  const location = useLocation();
  const doctorControlRequestId = useDoctorControlStore(state => state.activeRequest?.id ?? null);
  const identitySource = getMessagingIdentitySource({
    isDoctorControlMode,
    role: authContext?.userType ?? user?.user_type ?? user?.role ?? null,
    authUserId: user?.id ?? null,
    contextUserId: authContext?.userId ?? null,
    asPatientId: authContext?.asPatientId ?? user?.as_patient_id ?? null,
    asDoctorId: authContext?.asDoctorId ?? user?.as_doctor_id ?? null,
    actorRole: actorAuthContext?.userType ?? actorUser?.user_type ?? actorUser?.role ?? null,
    actorAuthUserId: actorUser?.id ?? null,
    actorContextUserId: actorAuthContext?.userId ?? null,
    actorAsPatientId: actorAuthContext?.asPatientId ?? actorUser?.as_patient_id ?? null,
    actorAsDoctorId: actorAuthContext?.asDoctorId ?? actorUser?.as_doctor_id ?? null,
  });
  const role = identitySource.role;
  const messagingUserId = getMessagingParticipantUserId(identitySource);
  const roomsQuery = useMessagingRooms({
    enabled: Boolean(isAuthenticated && !isLoading && user?.id),
  });
  const rooms = useMemo(() => roomsQuery.data?.rooms ?? [], [roomsQuery.data?.rooms]);
  const selectedRoomId = stripLocaleFromPathname(location.pathname).startsWith('/messages')
    ? new URLSearchParams(location.search).get('room')
    : null;
  const realtimeCacheScope = `${user?.id ?? 'anonymous'}:${
    doctorControlRequestId ? `doctor-control:${doctorControlRequestId}` : 'default'
  }`;
  const realtimeRoomIds = getMessagingRealtimeRoomIds(rooms, selectedRoomId);
  const authorizationRoomIds = useMemo(() => getMessagingAuthorizationRoomIds(rooms), [rooms]);
  const realtimeRoomIdSet = useMemo(() => new Set(realtimeRoomIds), [realtimeRoomIds]);
  const subscribedRooms = useMemo(
    () => rooms.filter(room => realtimeRoomIdSet.has(room.id)),
    [realtimeRoomIdSet, rooms]
  );

  return (
    <AblyChatProvider
      enabled={authorizationRoomIds.length > 0}
      authorizationRoomIds={authorizationRoomIds}
      cacheScope={realtimeCacheScope}
    >
      <RoomNotificationSubscriptions
        rooms={subscribedRooms}
        selectedRoomId={selectedRoomId}
        role={role ?? 'patient'}
        messagingUserId={messagingUserId}
      />
      {children}
    </AblyChatProvider>
  );
}
